import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { isInternalTech, PERMISSION_NAMES } from '../../utils/AuthUtils';
import moment from 'moment';
import { Button, message, Popconfirm, Popover } from 'antd';
import {
	compareStrings,
	getWorkOrderTimeZoneId,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import {
	INTERNAL_TECH_COMPLETED_STATUSES,
	SERVICE_PROVIDER_COMPLETED_STATUSES,
	WORK_ORDER_STATUSES,
} from '../../constants/WorkOrderStatuses';
import DateTimeHover from '../date_time_component/DateTime';
import {
	getSanitisedWorkOrder,
	isDifferentAssignedSupplier,
	isWOTaskCheckFailed,
} from '../../utils/WorkOrderUtils';
import { connect } from 'react-redux';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	taskItemsRestCrudThunksForBuyer,
	taskItemsRestCrudThunksForSupplier,
} from '../../thunks/task_items_thunks';

//Statuses which are allowed to be marked complete
const SERVICE_PROVIDER_COMPLETED_STATUS_EXCEPTIONS = [
	WORK_ORDER_STATUSES.workIncompleteWithReason,
	WORK_ORDER_STATUSES.workUnsatisfactory,
];

//Statuses which are not allowed to be marked as complete for third party suppliers
const THIRD_PARTY_SERVICE_PROVIDER_COMPLETED_STATUS = [WORK_ORDER_STATUSES.waitingForReview];

const ReviewAndMarkCompleteComponent: FC<any> = ({
	workOrder,
	showApproveModal,
	showReminderModal,
	isThirdPartySupplier,
	markWorkOrderCompleteByThirdParty,

	fetchTaskItems,
	currentUser,
	isClientView = true,
}): React.ReactElement => {
	const [completing, setCompleting] = useState(false);
	const [taskItems, setTaskItems] = useState<any[]>([]);
	const [taskItemsFetching, setTaskItemsFetching] = useState(false);

	const isInternalServiceTeamWO = useMemo(
		() => nullSafeGet('supplierFacility.status', workOrder) === 'private',
		[workOrder]
	);

	useEffect(() => {
		if (!workOrder.id) return;

		setTaskItemsFetching(true);
		fetchTaskItems({ workOrderId: workOrder.id, no_pagination: true })
			.then(setTaskItems)
			.catch(message.error)
			.finally(() => setTaskItemsFetching(false));
	}, [workOrder.id]);

	const question = useMemo(
		() =>
			isInternalServiceTeamWO
				? `Did your tech resolve this issue yet?`
				: `Did ${
						isThirdPartySupplier
							? 'you'
							: nullSafeGetOrElse(
									'supplierFacility.displayName',
									workOrder,
									'your service provider'
							  )
				  } resolve this issue yet?`,
		[isInternalServiceTeamWO, isThirdPartySupplier, workOrder]
	);

	const workOrderPastDue = useMemo(() => moment().diff(workOrder.dueDate) > 0, [workOrder.dueDate]);
	const canShowReviewButton = useMemo(
		() =>
			isInternalServiceTeamWO
				? !INTERNAL_TECH_COMPLETED_STATUSES.includes(workOrder.status)
				: SERVICE_PROVIDER_COMPLETED_STATUS_EXCEPTIONS.includes(workOrder.status) ||
				  ![
						...SERVICE_PROVIDER_COMPLETED_STATUSES,
						...(isThirdPartySupplier ? THIRD_PARTY_SERVICE_PROVIDER_COMPLETED_STATUS : []),
						WORK_ORDER_STATUSES.supplierInitiatedPendingApproval,
				  ].includes(workOrder.status),
		[isInternalServiceTeamWO, workOrder.status, isThirdPartySupplier]
	);
	const onThirdPartyMarkComplete = useCallback(() => {
		setCompleting(true);
		markWorkOrderCompleteByThirdParty().finally(() => setCompleting(false));
	}, [markWorkOrderCompleteByThirdParty]);

	const tasksCheckFailed = useMemo(
		() => taskItemsFetching || isWOTaskCheckFailed(workOrder, taskItems),
		[taskItems, taskItemsFetching, workOrder]
	);

	const hideReviewButtonDueToCheckIn = useMemo(() => {
		const isBuyer = currentUser.userType === ROLE_TYPES.BUYER;
		const isInternalTechAndDifferentAssignedSupplier =
			isInternalTech(currentUser) && isDifferentAssignedSupplier(workOrder, currentUser);
		const techWorkingOnSite = compareStrings(
			nullSafeGet('status', getSanitisedWorkOrder(workOrder, isClientView)),
			WORK_ORDER_STATUSES.techWorkingOnSite
		);
		return isBuyer || isInternalTechAndDifferentAssignedSupplier
			? false
			: isClientView && techWorkingOnSite;
	}, [currentUser, isClientView, workOrder]);

	if (hideReviewButtonDueToCheckIn) {
		return null;
	}

	return canShowReviewButton ? (
		<AccessPermissionChecker name={PERMISSION_NAMES.CAN_MARK_WORK_ORDER_COMPLETED}>
			<div>
				<h5>{question}</h5>
				{workOrderPastDue ? (
					<div style={{ fontSize: 16, maxWidth: 480 }}>
						This work was due{' '}
						<DateTimeHover
							timestamp={nullSafeGet('dueDate', workOrder)}
							timeZone={getWorkOrderTimeZoneId(workOrder)}
						/>
						.
					</div>
				) : null}
				<div style={{ marginTop: 16, marginBottom: 40 }}>
					{isThirdPartySupplier ? (
						<Popover
							content={
								tasksCheckFailed
									? 'All tasks need to be completed before marking this complete!'
									: ''
							}
							trigger="hover"
						>
							<Popconfirm
								onConfirm={onThirdPartyMarkComplete}
								placement="topRight"
								title="Are you sure you want to mark this complete?"
								okText="Yes"
								cancelText="No"
							>
								<Button
									disabled={tasksCheckFailed}
									type="primary"
									key="approve"
									loading={completing}
								>
									Yes, mark complete
								</Button>
							</Popconfirm>
						</Popover>
					) : (
						<Button type="primary" key="approve" onClick={showApproveModal}>
							{`Yes, ${
								isInternalServiceTeamWO || isThirdPartySupplier ? '' : 'review and'
							} mark complete`}
						</Button>
					)}
					{isThirdPartySupplier ? null : (
						<Button key="reminder" style={{ marginLeft: 16 }} onClick={showReminderModal}>
							No, send a reminder
						</Button>
					)}
				</div>
			</div>
		</AccessPermissionChecker>
	) : null;
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchTaskItems: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? taskItemsRestCrudThunksForSupplier.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: taskItemsRestCrudThunksForBuyer.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
});

const ComponentWithoutUserType = connect(
	mapStateToProps,
	mapDispatchToProps
)(ReviewAndMarkCompleteComponent);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
